// send-feedback 1.1.0, copied from ~/Projects/send-feedback by its install.sh. Change it there and reinstall; edits here are overwritten.
// Send feedback: a bug report or an idea, typed into an app's Settings, becomes an issue in the app's GitHub
// repo, where the owner gets GitHub's email and the Claude runner can pick it up.
//
// What is the same in every app lives here: the two kinds and their wording, checking a report, the issue's
// title, body and labels, filing it, the hourly limit, a short device description, and sending from the app so
// that trying again never files the same report twice. What differs stays in the
// app: who the sender is (its own sign-in and membership check), where reports are kept, and the form itself.
// The GitHub token can never be in the app, so filing always happens on the app's server.
//
// The issue body carries a "| From | <name> |" row and a signature line, which is how the Claude runner's
// `feedback` setting tells these issues (all opened with the owner's token) apart and names who sent each.
//
// No imports and no DOM types, so the same source runs in a browser page, a bundled app and Node.

export type FeedbackKind = 'bug' | 'idea'

/** The two kinds, in the words the form and the issue use. */
export const KINDS: Record<FeedbackKind, { label: string; prompt: string; title: string }> = {
  bug: {
    label: "Something's broken",
    prompt: 'What happened, and what did you expect instead? The steps that make it happen again help most.',
    title: 'Bug',
  },
  idea: { label: 'An idea', prompt: 'What would you like the app to do?', title: 'Idea' },
}

export const MAX_TEXT = 5000
export const MIN_TEXT = 3
/** What a report id may be: safe as a Firestore document id or a Realtime Database key. */
const ID = /^[A-Za-z0-9_-]{8,64}$/

export interface Report {
  kind: FeedbackKind
  text: string
  /** Who sent it, as the app knows them: a first name, or a name and a household tag. Never an email. */
  from: string
  /** What the app attaches when "Include app details" is ticked, as label → value: Version, Screen, Device. */
  details?: Record<string, string>
}

export interface Issue {
  title: string
  body: string
  labels: string[]
}

/** Control characters out, trimmed and cut to length. */
export function clip(value: unknown, max: number): string {
  // control characters (keeping tab and newline) become spaces; a loop rather than a regex lints clean everywhere
  const text = Array.from(String(value ?? ''), (ch) => {
    const c = ch.charCodeAt(0)
    return (c < 32 && c !== 9 && c !== 10) || c === 127 ? ' ' : ch
  }).join('')
  return text.trim().slice(0, max)
}

/**
 * A report's kind, text and id from untrusted input, or what is wrong with it. The id is the app's, from
 * `reportId()`, and optional: a server that has already kept a report under it answers "sent" again rather than
 * filing it twice.
 */
export function checkReport(
  input: unknown,
): { ok: true; kind: FeedbackKind; text: string; id?: string; details?: Record<string, string> } | { ok: false; error: string } {
  const b = (input && typeof input === 'object' ? input : {}) as { kind?: unknown; text?: unknown; id?: unknown; details?: unknown }
  if (b.kind !== 'bug' && b.kind !== 'idea') return { ok: false, error: 'kind must be bug or idea' }
  if (b.id !== undefined && (typeof b.id !== 'string' || !ID.test(b.id))) return { ok: false, error: 'bad id' }
  const text = String(b.text ?? '').replace(/\r\n?/g, '\n').trim()
  if (text.length < MIN_TEXT) return { ok: false, error: 'too short' }
  if (text.length > MAX_TEXT) return { ok: false, error: 'too long' }
  let details: Record<string, string> | undefined
  if (b.details && typeof b.details === 'object') {
    // a handful of short labelled values; anything else is dropped
    details = {}
    for (const [k, v] of Object.entries(b.details as Record<string, unknown>).slice(0, 8)) {
      const key = clip(k, 20)
      if (key && (typeof v === 'string' || typeof v === 'number')) details[key] = clip(v, 200)
    }
  }
  return { ok: true, kind: b.kind, text, ...(typeof b.id === 'string' ? { id: b.id } : {}), ...(details ? { details } : {}) }
}

/** The default signature: the line that marks an issue as sent from the app. */
export function signatureFor(app: string): string {
  return `Sent from ${app} → Settings → Send feedback`
}

/**
 * The GitHub issue for a report: "[Bug] <first line>" as the title, the text quoted, a table of who sent it and
 * the app details, and the signature. `@` is defused so a report cannot ping GitHub users.
 */
export function issueFor(report: Report, options: { app: string; signature?: string }): Issue {
  const first = report.text.split('\n').find((l) => l.trim()) ?? report.text
  const title = `[${KINDS[report.kind].title}] ${first.length > 70 ? first.slice(0, 69).trimEnd() + '…' : first}`
  const quote = report.text
    .replace(/@/g, '@​')
    .split('\n')
    .map((l) => '> ' + l)
    .join('\n')
  const cell = (s: string) => (s || '—').replace(/\|/g, '\\|').replace(/\n/g, ' ')
  const rows: [string, string][] = [['From', clip(report.from, 80) || 'someone'], ...Object.entries(report.details ?? {})]
  const body = `${quote}\n\n| | |\n|---|---|\n${rows.map(([k, v]) => `| ${cell(k)} | ${cell(v)} |`).join('\n')}\n\n<sub>${options.signature ?? signatureFor(options.app)}</sub>`
  return { title, body, labels: ['feedback', report.kind] }
}

/**
 * Files the issue and returns its number. `token` is a fine-grained GitHub token for `repo` ("owner/name") with
 * Issues: read and write, and nothing else. A label GitHub will not apply (422) is dropped rather than losing the
 * report.
 */
export async function fileIssue(args: { token: string; repo: string; issue: Issue; userAgent?: string; fetch?: typeof fetch }): Promise<number> {
  const doFetch = args.fetch ?? fetch
  const post = (payload: Partial<Issue>) =>
    doFetch(`https://api.github.com/repos/${args.repo}/issues`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${args.token}`,
        Accept: 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
        'User-Agent': args.userAgent ?? 'send-feedback',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
  let res = await post(args.issue)
  if (res.status === 422) res = await post({ title: args.issue.title, body: args.issue.body })
  if (!res.ok) throw new Error(`GitHub ${res.status}: ${(await res.text()).slice(0, 300)}`)
  return ((await res.json()) as { number: number }).number
}

/** True when the sender has already sent `perHour` reports in the last hour. `recent` is their send times, in ms. */
export function overLimit(recent: number[], options: { perHour?: number; now?: number } = {}): boolean {
  const perHour = options.perHour ?? 5
  const now = options.now ?? Date.now()
  return recent.filter((t) => t > now - 3600_000).length >= perHour
}

/**
 * A fresh id for a report, made in the app when someone presses Send and kept for every retry of the same words,
 * so the server can tell a retry from a new report. Unique enough within one sender's reports.
 */
export function reportId(now = Date.now(), random = Math.random): string {
  const part = () => Math.floor(random() * 36 ** 6).toString(36).padStart(6, '0')
  return now.toString(36) + part() + part()
}

/**
 * Sends a report from the app, trying again on its own when the failure may only be the connection. On a phone a
 * request often dies when the app goes to the background, even though the server already has the report; the
 * retries carry the same id, so the server answers "sent" instead of filing it again, and the person never sees
 * "didn't send" for a report that went. `send` makes one attempt; `retryable` says whether a failure is worth
 * another (a connection error, not a refusal); `waits` are the pauses before each retry; `ready` resolves when it
 * is sensible to try, such as once the app is in front again.
 */
export async function sendWithRetry<T>(
  send: () => Promise<T>,
  options: { retryable: (error: unknown) => boolean; waits?: number[]; ready?: () => Promise<void>; sleep?: (ms: number) => Promise<void> },
): Promise<T> {
  const waits = options.waits ?? [1000, 3000]
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)))
  for (let attempt = 0; ; attempt++) {
    try {
      return await send()
    } catch (e) {
      if (attempt >= waits.length || !options.retryable(e)) throw e
      await sleep(waits[attempt])
      if (options.ready) await options.ready()
    }
  }
}

/** "Chrome 141 on Android", from a user-agent string. */
export function describeDevice(ua: string): string {
  const b = /(Edg|OPR|CriOS|FxiOS|Firefox|Chrome)\/(\d+)/.exec(ua) ?? (/Safari\//.test(ua) ? /(Version)\/(\d+)/.exec(ua) : null)
  const browser = b ? `${{ Edg: 'Edge', OPR: 'Opera', CriOS: 'Chrome', FxiOS: 'Firefox', Version: 'Safari' }[b[1]] ?? b[1]} ${b[2]}` : 'A browser'
  // in order of priority: an Android phone's string also says Linux, an iPhone's also says Mac OS X
  const os = (['iPhone', 'iPad', 'Android', 'CrOS', 'Windows', 'Mac OS X', 'Linux'].find((name) => ua.includes(name)) ?? '').replace('Mac OS X', 'macOS').replace('CrOS', 'ChromeOS')
  return os ? `${browser} on ${os}` : browser
}
